import { create } from 'zustand';
import type { CompanyFeatures, CompanyRef } from '../lib/types';

// The active company is a UI preference, not an authority boundary: every request still
// sends it in X-Company-Id and the server validates access. Keeping the preference here
// lets a browser reload restore the company context before the first /auth/session call.
// Storage can be unavailable (private browsing or a locked-down webview), so it must
// remain an optional convenience rather than a dependency for authentication.
const ACTIVE_COMPANY_STORAGE_KEY = 'khata.active-company-id';

function readActiveCompanyId(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(ACTIVE_COMPANY_STORAGE_KEY);
  } catch {
    return null;
  }
}

function persistActiveCompanyId(id: string | null): void {
  if (typeof window === 'undefined') return;
  try {
    if (id) window.localStorage.setItem(ACTIVE_COMPANY_STORAGE_KEY, id);
    else window.localStorage.removeItem(ACTIVE_COMPANY_STORAGE_KEY);
  } catch {
    // A usable in-memory session is still preferable to failing because persistence is
    // disabled by the browser.
  }
}

interface CompanyState {
  companies: CompanyRef[];
  activeCompanyId: string | null;
  features: CompanyFeatures | null;

  setCompanies(companies: CompanyRef[]): void;
  setActiveCompanyId(id: string | null): void;
  setFeatures(features: CompanyFeatures | null): void;
  activeCompany(): CompanyRef | null;
}

export const useCompanyStore = create<CompanyState>((set, get) => ({
  companies: [],
  activeCompanyId: readActiveCompanyId(),
  features: null,

  setCompanies(companies) {
    set({ companies });
  },

  setActiveCompanyId(id) {
    persistActiveCompanyId(id);
    set({ activeCompanyId: id, features: null });
  },

  setFeatures(features) {
    set({ features });
  },

  activeCompany() {
    const { companies, activeCompanyId } = get();
    return companies.find((c) => c.id === activeCompanyId) ?? null;
  },
}));
